import { Component, Input, OnInit } from '@angular/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { BaseComponent } from '../../../shared/utils/base.component';
import { SectionsService } from '../../../services/sections.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ReferralDataService } from '../../../services/referral-data.service';
import { NzFormModule } from 'ng-zorro-antd/form';
import { ListTableComponent, UploadComponent } from '@ppl/ui';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../shared/constants/static-data.constants';
import { CommonModule } from '@angular/common';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { FileUploadComponent } from '../../lead-details/documents/file-upload/file-upload/file-upload.component';
import { SharedService } from '../../../shared/services/shared.service';
import { SharedDataService } from '../../../shared/services/shared-data-service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UploadHandlerService } from '../../../shared/services/upload-handler.service';
import { ButtonComponent } from '@ppl/ui';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { AutoFocusFirstInputDirective } from '../../../shared/directives/auto-focus-first-input.directive';
import { missingParentDocCodes } from '../participant-information/parent-guardian/parent-guardian.util';

@Component({
  selector: 'ppl-upload-docs',
  standalone: true,
  imports: [
    UploadComponent,
    NzCardModule,
    NzGridModule,
    NzFormModule,
    ReactiveFormsModule,
    ListTableComponent, CommonModule,
    FileUploadComponent,
  ],
  templateUrl: './upload-docs.component.html',
  styleUrl: './upload-docs.component.css',
})
export class UploadDocsComponent extends BaseComponent implements OnInit {
  @Input() mode = 'edit';
  licences: any[] = [];
  uploadDocsForm!: FormGroup;
  fileList: any = [];
  formHasBeenFilled: any;
  filledInfo: any = {};
  cols: any[] = [];
  flow: any;
  leadId: any;
  steps: any;
  showFileList = false;
  // Missing required parent/guardian document labels (PAR1..PARn), shown when the step is blocked.
  docErrors: string[] = [];

  constructor(
    private sectionsService: SectionsService,
    private referralDataService: ReferralDataService,
    private fb: FormBuilder,
    private userService: UserService,
    private sharedService: SharedService,
    private sharedDataService: SharedDataService,
    private notificationService: NotificationService,
    private uploadHandler: UploadHandlerService,
    private storageService: StorageService
  ) {
    super();
  }

  ngOnInit(): void {

    this.getFileCols();

    this.uploadDocsForm = this.fb.group({
      filesUploaded: [[]],
    });
    const program = this.userService.getSelectedProgram();
    this.steps = this.sectionsService.getSectionData(program?.program_code);
    this.secData = this.steps['upload_docs'];

    const enrollType = this.sectionsService.getEnrollmentType();
    const leadEntry = this.userService.getLeadEntry();
    if (leadEntry === 'referral') {
      this.licences = this.getLookup('License_Type_Referral');
    } else if (leadEntry === 'participantEnrollment') {
      this.licences = this.getLookup('Upload_Doc_Type');
      // filter out parent/guardian documents if the participant is an adult (age >= 18)
      const dob = this.referralDataService.getReferralFormData()?.infoData?.dob;
      const rel = this.referralDataService.getReferralFormData()?.infoData?.relationshipInformation?.filter((r: any) => !r.notApplicable && r.fname && r.lname) || [];
      // hide the par1, par2, par3, par4 codes based on the number of parent/guardian records in the relationshipInformation array
      
      // If the participant is an adult, hide all parent/guardian document types (PAR1..PAR4) regardless of relationshipInformation entries.
      
      if (!this.isMinor(dob)) {
        this.licences = this.licences.filter((license: any) => {
          const docCode = license.lkpValueCode;
          return !docCode.startsWith('PAR');
        });
      } else {
        const requiredDocs = Array.from({ length: rel.length }, (_, i) => `PAR${i + 1}`);
        this.licences = this.licences.filter((license: any) => {
          const docCode = license.lkpValueCode;
          return !docCode.startsWith('PAR') || requiredDocs.includes(docCode);
        });
      }
    } else if (enrollType && Array.isArray(enrollType)) {
      // Map each enrollment type to its document type domain
      const domainMap: Record<string, string> = {
        'provider': 'License_Type_IP',
        'vendor': 'License_Type_Vendor',
        'contractor': 'License_Type_IC',
        'employer': 'License_Type_Employer',
        'independentFacilitator': 'License_Type_IF',
        'authRepresentative': 'License_Type_AR',
      };

      // Collect doc types from all enrolled types, dedup by lkpValueCode
      const uniqueTypes = [...new Set<string>(enrollType)];
      const docTypeMap = new Map<string, any>();
      for (const type of uniqueTypes) {
        const domain = domainMap[type];
        if (domain) {
          const lookupValues = this.getLookup(domain) ?? [];
          for (const item of lookupValues) {
            if (!docTypeMap.has(item.lkpValueCode)) {
              docTypeMap.set(item.lkpValueCode, item);
            }
          }
        }
      }
      this.licences = docTypeMap.size > 0 ? Array.from(docTypeMap.values()) : this.getLookup('License_Type');

      // if enrollType includes conservator, legal guardian or poa then show only 'Other' document type
      if (this.sectionsService.getFlow() === STATIC_DATA.PROVIDER_ENROLLMENT &&
        (enrollType.includes('legalGuardian') || enrollType.includes('poa') || enrollType.includes('conservator'))) {
        this.licences = this.licences.filter((license: any) => license.lkpValueCode === 'OTH');
      }
    } else {
      this.licences = this.getLookup('License_Type');
    }

    this.flow = this.sectionsService.getFlow();


    this.uploadDocsForm.valueChanges.subscribe(() => {
      this.fileList = this.uploadDocsForm.value.filesUploaded;
      const hasValue = this.hasAnyValueInForm(this.uploadDocsForm);
      this.formHasBeenFilled = hasValue;
      this.referralDataService.setReferralFormData(
        'isUploadFormUpdated',
        this.formHasBeenFilled
      );

      this.referralDataService.setReferralFormData(
        'filesUploaded',
        this.uploadDocsForm.value
      );
      this.referralDataService.setReferralFormData(
        'isUploadFormDirty',
        this.uploadDocsForm.dirty
      );
      if (this.fileList.length > 0) {
        this.referralDataService.setReferralFormData('isUploadFormDirty', true);
      }
    });

    if (
      this.sectionsService.getFlow() == 'referral' &&
      this.userService.getLeadEntry() == 'referral' &&
      !this.referralDataService.getReferralFormData().filesUploaded
    ) {
      const filledData =
        this.referralDataService.getReferralDetails('filesUploaded')!;
      if (filledData) {
        this.uploadDocsForm.patchValue(filledData);
      }
    }

    if (this.referralDataService.getReferralFormData().filesUploaded) {
      this.filledInfo =
        this.referralDataService.getReferralFormData().filesUploaded;
      this.uploadDocsForm.patchValue(this.filledInfo);
    }

    this.referralDataService.nextClicked.subscribe((val: any) => {
      if (val !== 'upload_docs') return;
      const missing = this.getMissingParentDocs();
      this.docErrors = missing.map((code) => this.docLabel(code));
      this.referralDataService.setError(
        missing.length ? { parentDocs: 'Required parent/guardian documents missing' } : []
      );
    });
    if (this.flow === STATIC_DATA.REFERRAL) {
      this.showFileList = true;
    }
    this.leadId = '';
    if (this.flow === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
      this.leadId = this.storageService.getParticipantId();
      this.getDocumentsUploaded();
    }
    if (this.flow === STATIC_DATA.PROVIDER_ENROLLMENT) {
      this.leadId = this.storageService.getProviderId()
      this.getDocumentsUploaded();
    }
    if (this.flow === STATIC_DATA.REFERRAL) {
      this.leadId = this.storageService.getReferralId();
    }
  }

  filesUpdated() {
    console.log('Files List update');
    this.getDocumentsUploaded();
  }

  filesUploaded(files: any) {
    this.uploadDocsForm.patchValue({
      filesUploaded: files,
    });
    if (this.flow === STATIC_DATA.PARTICIPANT_ENROLLMENT || this.flow === STATIC_DATA.PROVIDER_ENROLLMENT) {
      this.getDocumentsUploaded();
    }
  }

  // Parent handles actual upload to backend via SharedService
  onFileSelected(event: any) {
    const file = event?.file as File;
    if (!file) return;

    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const type = this.flow || this.userService.getLeadEntry();
    const flowName = (type === 'authorizations') ? 'auth' : this.sharedDataService.getType(type);

    const formData = new FormData();
    formData.append('file', file as any);

    const requestPayload: any = {
      fileType: file.name.split('.').pop() || '',
      otherType: event.documentType || '',
      program,
      type: flowName,
      ...(flowName === 'auth' ? { authDetailId: event.leadId } : { leadId: event.leadId })
    };

    if ((event.consumerId && event.providerId) || (event.source === 'purchaserequest' && event.consumerId)) {
      Object.assign(requestPayload, {
        consumerId: event.consumerId,
        providerId: event.providerId,
        type: event.source || type
      });
    }

    formData.append('json', new Blob([JSON.stringify(requestPayload)], { type: 'application/json' }));

    // need to update files list when file is uploaded successfully. So passing the callback to service which will be called after successful upload.
    this.uploadHandler.uploadFromEvent(event, type).subscribe({
      next: (response: any) => {
        if (this.leadId) {
          this.getDocumentsUploaded();
        } else {
          // New referral flow — no leadId yet; build the file entry from the upload response
          const id = response?.responsedata?.file_upload_tracking_number ?? Date.now();
          const newFile = { id, name: file.name, size: file.size, type: event.documentType };
          const updatedFiles = [...(this.fileList || []), newFile];
          this.fileList = updatedFiles;
          this.uploadDocsForm.patchValue({ filesUploaded: updatedFiles });
        }
      },
      error: (_err: any) => this.notificationService.error('Error', `${file.name} file upload failed.`)
    });
  }

  onFileRemoved(fileId: any) {
    const id = fileId;
    const createType = this.leadId ? 'detail' : 'new';
    const docSource = this.flow || '';
    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    this.uploadHandler.deleteFile(program, id, createType, docSource).subscribe(() => {
      if (this.leadId) {
        this.getDocumentsUploaded();
      } else {
        const updatedFiles = (this.fileList || []).filter((f: any) => f.id !== id);
        this.fileList = updatedFiles;
        this.uploadDocsForm.patchValue({ filesUploaded: updatedFiles });
      }
    });
  }

  onDownloadRequested(file: any) {
    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const docSource = this.flow || '';
    this.uploadHandler.downloadFile(file, docSource);
  }

  onUploadError(payload: any) {
    this.uploadHandler.handleUploadError(payload);
  }

  async getFileCols() {
    const tempCols = await this.sectionsService.getJsonConfigurationData(
      STATIC_JSON_DATA_PATH.FILE_UPLOAD_HEADERS
    );
    this.cols = tempCols?.documents?.columns;
  }

  hasUploadedFile(): boolean {
    return (
      this.filledInfo['filesUploaded'] &&
      this.filledInfo['filesUploaded'].length > 0
    );
  }

  getDocumentsUploaded() {
    if (!this.leadId) return;
    const program = this.userService.getSelectedProgram();
    const flow = this.sectionsService.getFlow()!;
    this.sharedService.getUploadedFileDetails(program?.program_code ?? '', this.leadId, flow).subscribe((data) => {
      this.filledInfo = data?.responsedata;
      // For referral flow the file list is displayed via ppl-upload using fileList/form binding,
      // so patch the form with the fetched file list to keep it in sync.
      if (this.flow === STATIC_DATA.REFERRAL) {
        const files = this.filledInfo?.filesUploaded ?? [];
        this.fileList = files;
        this.uploadDocsForm.patchValue({ filesUploaded: files });
      }
      this.syncUploadedDocTypes();
    });
  }

  // Publish the uploaded document-type codes for the registration (Submit) gate, and refresh the
  // missing-doc display so it clears as required parent docs are added.
  private syncUploadedDocTypes(): void {
    if (this.flow !== STATIC_DATA.PARTICIPANT_ENROLLMENT) return;
    const codes = (this.filledInfo?.participantEnrlDocumentDetails || [])
      .map((d: any) => d.otherType)
      .filter(Boolean);
    this.referralDataService.setReferralFormData('uploadedDocTypes', codes);
    this.docErrors = this.getMissingParentDocs().map((code) => this.docLabel(code));
  }

  // Required parent/guardian doc-type codes (PAR1..PARn) not yet uploaded. Participant enrollment only.
  private getMissingParentDocs(): string[] {
    if (this.flow !== STATIC_DATA.PARTICIPANT_ENROLLMENT) return [];
    const info = this.referralDataService.getReferralFormData()?.infoData;
    const rel = info?.relationshipInformation || [];
    const uploaded = (this.filledInfo?.participantEnrlDocumentDetails || [])
      .map((d: any) => d.otherType)
      .filter(Boolean);
    // Adults are exempt — the PAR doc types are filtered out of `licences` above, so requiring them
    // would block the step on documents the user has no way to upload.
    return missingParentDocCodes(rel, uploaded, this.isMinor(info?.dob));
  }

  private docLabel(code: string): string {
    const found = (this.licences || []).find((l: any) => l.lkpValueCode === code);
    return found?.lkpValueName || code;
  }
}
